/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosResponse, Method } from 'axios';
import { LuisRequestOptions } from 'src/core/types/LuisRequestOptions';
import { httpClient } from 'src/core/utils/api/httpClient';

/**
 * Represents a non opinionated and stateless http service for
 * the entire portal to use.
 */
export const httpService = {
    /**
     * Performs a GET web call using the given url and options.
     *
     * @param url The url to use for the web call.
     * @param options The options to use for the web call.
     * @returns The response received from the web call.
     */
    get: async <T>(url: string, options?: LuisRequestOptions): Promise<AxiosResponse<T>> => httpClient.get<T>(url, options),

    /**
     * Performs a POST web call using the given url and options.
     *
     * @param url The url to use for the web call.
     * @param data The data to send in the web call.
     * @param options The options to use for the web call.
     * @returns The response received from the web call.
     */
    post: async <T>(url: string, data: Object, options?: LuisRequestOptions): Promise<AxiosResponse<T>> =>
        httpClient.post(url, data, options),

    /**
     * Performs a PUT web call using the given url and options.
     *
     * @param url The url to use for the web call.
     * @param data The data to send in the web call.
     * @param options The options to use for the web call.
     * @returns The response received from the web call.
     */
    put: async <T>(url: string, data: Object, options?: LuisRequestOptions): Promise<AxiosResponse<T>> =>
        httpClient.put(url, data, options),

    /**
     * Performs a PATCH web call using the given url and options.
     *
     * @param url The url to use for the web call.
     * @param data The data to send in the web call.
     * @param options The options to use for the web call.
     * @returns The response received from the web call.
     */
    patch: async <T>(url: string, data: Object, options?: LuisRequestOptions): Promise<AxiosResponse<T>> =>
        httpClient.patch(url, data, options),

    /**
     * Performs a DELETE web call using the given url and options.
     *
     * @param url The url to use for the web call.
     * @param options The options to use for the web call.
     * @returns The response received from the web call.
     */
    delete: async <T>(url: string, options?: LuisRequestOptions): Promise<AxiosResponse<T>> => httpClient.delete(url, options),

    /**
     * Performs a POST web call using the given url and file
     * upload options.
     *
     * @param url The url to use for the web call.
     * @param data The data to send in the web call.
     * @param options The options to use for the web call.
     * @returns The response received from the web call.
     */
    upload: async <T>(url: string, data: Object, options: LuisRequestOptions): Promise<AxiosResponse<T>> =>
        httpClient.post(url, data, options),

    /**
     * Performs a GET web call using the given url and file
     * download options.
     *
     * @param url The url to use for the web call.
     * @param options The options to use for the web call.
     * @returns The response received from the web call as
     * a blob.
     */
    download: async (url: string, options: LuisRequestOptions): Promise<AxiosResponse<Blob>> =>
        httpClient.get(url, { ...options, responseType: 'blob' }),

    /**
     * Performs a custom web call using the given http method, url and options.
     *
     * @param method The method to use for the web call.
     * @param url The url to use for the web call.
     * @param options The options to use for the web call.
     * @returns The response received from the web call.
     */
    request: async <T>(method: Method, url: string, data: string, options?: LuisRequestOptions): Promise<AxiosResponse<T>> =>
        httpClient.request({
            method,
            url,
            data,
            ...options
        })
};
